import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/i18n';

// PWA Install prompt interface
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPromptDialog() {
  const { currentLang } = useI18n();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallDialogOpen, setIsInstallDialogOpen] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const showPrompt = () => setIsInstallDialogOpen(true);
    window.addEventListener('show-install-prompt', showPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('show-install-prompt', showPrompt);
    };
  }, []);

  useEffect(() => {
    if (deferredPrompt) {
      const timer = setTimeout(() => {
        setIsInstallDialogOpen(true);
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [deferredPrompt]);

  const handleInstallApp = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult: { outcome: 'accepted' | 'dismissed' }) => {
        if (choiceResult.outcome === 'accepted') {
          setDeferredPrompt(null);
        }
        setIsInstallDialogOpen(false);
      });
    }
  };

  return (
    <Dialog open={isInstallDialogOpen} onOpenChange={setIsInstallDialogOpen}>
      <DialogContent className="sm:max-w-md border-none bg-white/95 backdrop-blur-xl">
        <DialogHeader className="mb-2">
          <div className="mx-auto -m-8 w-[200px] h-[200px] rounded-none overflow-hidden flex items-center justify-center">
            <img src="/images/logo-png.png" alt="App Icon"/>
          </div>
          <DialogTitle className="text-center -mx-2 text-2xl - font-black">
            {currentLang === 'bn' ? 'চকোলেট ভাইবস অ্যাপ ইনস্টল করুন' : 'Install Chocolate Vibes'}
          </DialogTitle>
          <DialogDescription className="text-center text-base text-gray-700 font-medium">
            {currentLang === 'bn' 
              ? 'দ্রুত অর্ডার, এক্সক্লুসিভ অফার এবং অফলাইন সাপোর্ট পেতে আমাদের অ্যাপটি আপনার ফোনে ইনস্টল করুন।' 
              : 'Install our app for faster ordering, exclusive offers, and offline support on your phone.'}
          </DialogDescription>
        </DialogHeader>
        
        <DialogFooter className="flex-col sm:flex-col gap-2 pt-2">
          <Button onClick={handleInstallApp} className="w-full text-base font-bold py-6 rounded-2xl shadow-lg shadow-primary/20">
            {currentLang === 'bn' ? 'এখনই ইনস্টল করুন' : 'Install Now'}
          </Button>
          <Button onClick={() => setIsInstallDialogOpen(false)} variant="ghost" className="w-full font-medium hover:bg-transparent">
            {currentLang === 'bn' ? 'পরে' : 'Maybe Later'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
